import type { FileFetchingDetails, IntegrationData, PlatformIntegrationData } from "../Integrations.types"
import {
	integrationListCVList,
	integrationGetCVContent,
	integrationGetUserDetails,
	integrationSetupAddIntegration,
	integrationSetupDeleteIntegration,
	integrationSetupGetIntegration,
} from "shared/src/libs/apiLib/apiLib"
import { CenteredStatus } from "../../CenteredStatus/CenteredStatus"
import { useCallback, useState } from "react"
import { useMountEffect } from "../../../libs/hooksLib/hooksLib"
import { useAppContext } from "../../../libs/contextLib/contextLib"
import { Button } from "../../shadcn/components/ui/button"

export const jobAdderClientId = process.env.VITE_JOBADDER_CLIENT_ID ?? "4kvs4kk53ueutowfz6ytmaqcwe"

export function AddJobAdderIntegration({}) {
	const { company } = useAppContext()
	const [integrationExists, setIntegrationExists] = useState(false)
	const [loading, setLoading] = useState(true)
	const [finished, setFinished] = useState(false)
	const [message, setMessage] = useState<string | null>("Fetching integration details...")

	const getIntegration = useCallback(async () => {
		const integrations = await integrationSetupGetIntegration({ platform: "jobadder" }, company.id).catch((e) => {
			return null
		})
		if (integrations?.length > 0 && integrations[0].actionId != null) {
			setIntegrationExists(true)
		}
		setLoading(false)
	}, [company.id])

	useMountEffect(() => {
		getIntegration()
	})

	const addIntegration = useCallback(async () => {
		setLoading(true)
		setMessage("Adding JobAdder integration...")
		await integrationSetupAddIntegration({ platform: "jobadder" }, company.id)
			.catch((e) => {
				console.error("Error adding JobAdder integration", e)
				alert("There was an error adding the JobAdder integration. Please try again.")
			})
			.then(() => {
				// close window when finished
				// window.close()
				setIntegrationExists(true)
				setMessage("JobAdder integration added. You can close this window.")
				setFinished(true)
			})
			.finally(() => {
				setLoading(false)
			})
	}, [company.id])

	const deleteIntegration = useCallback(async () => {
		if (!window.confirm("Are you sure you want to delete the JobAdder integration?")) {
			return
		}
		setLoading(true)
		setMessage("Deleting JobAdder integration...")
		await integrationSetupDeleteIntegration({ platform: "jobadder" }, company.id)
			.catch((e) => {
				console.error("Error deleting JobAdder integration", e)
				alert("There was an error deleting the JobAdder integration. Please try again.")
			})
			.then(() => {
				setIntegrationExists(false)
				setMessage("JobAdder integration deleted. You can close this window.")
				setFinished(true)
				window.close() //close window when finished as authorisation needs to be rerun before anything else can happen
			})
			.finally(() => {
				setLoading(false)
			})
	}, [company.id])

	if (loading) {
		return <CenteredStatus isLoading={loading && !finished}>{message}</CenteredStatus>
	} else {
		return (
			<CenteredStatus isLoading={false}>
				<div
					style={{ display: "flex", flexDirection: "column", gap: 15, justifyContent: "center", alignItems: "center" }}>
					<div style={{ maxWidth: "80vw", textAlign: "center" }}>
						Your admin user has been successfully authenticated for the HireAra integration. If you don't need to make
						any changes, you can now just close this window.
					</div>
					<div>Click here to continue and {integrationExists ? "delete" : "add"} the HireAra-JobAdder integration:</div>
					<div>
						<Button size="lg" onClick={integrationExists ? deleteIntegration : addIntegration} variant="default">
							{integrationExists ? "Delete Integration" : "Add Integration"}
						</Button>
					</div>
				</div>
			</CenteredStatus>
		)
	}
}

export const jobAdderIntegrationData: PlatformIntegrationData<
	"oauthLogin" | "setupUser" | "candidateProfile" | "chooseFile" | "fetchFile" | "confirmationPage"
> = {
	allowChat: true,
	parameterMapping: {
		clientId: "clientId",
		externalClientId: "externalClientId",
		externalId: "externalId",
		userId: "userId",
	},
	pages: ["oauthLogin", "setupUser", "candidateProfile", "chooseFile", "fetchFile", "confirmationPage"],
	setupPages: ["oauthLogin", AddJobAdderIntegration],
	props: {
		oauthLogin: {
			newTab: false,
			createStateData: ({ sig, integrationData, clientId }) => ({ sig, integrationData, clientId }),
			getLoginUrl: (integrationData: IntegrationData, state?: string) => {
				alert("The integration has not been set up correctly. Please contact support.")
				throw new Error("JobAdder integration not set up correctly")
				// const stateQueryString = state ? `&state=${encodeURIComponent(state)}` : ""
				// return `https://id.jobadder.com/connect/authorize?client_id=${jobAdderClientId}&response_type=code&scope=offline_access%20read_candidate%20write_candidate%20read_jobapplication%20write_jobapplication%20read_user&redirect_uri=${window.location.origin}/oauthcallback${stateQueryString}`
			},
		},
		setupUser: {
			getUserDetails: async (clientId: string, integrationData: IntegrationData) => {
				return await integrationGetUserDetails(integrationData, clientId)
			},
		},
		fetchFile: {
			getFile: async (clientId: string, integrationData: IntegrationData, fileFetchingDetails: FileFetchingDetails) => {
				return await integrationGetCVContent(integrationData, fileFetchingDetails, clientId)
			},
		},
		chooseFile: {
			listFiles: async (clientId: string, integrationData: IntegrationData) => {
				return await integrationListCVList(integrationData, clientId)
			},
		},
		confirmationPage: {},
		candidateProfile: {},
	},
	setupProps: {
		oauthLogin: {
			newTab: true,
			createStateData: ({ integrationData, clientId }) => ({ integrationData, clientId }),
			getLoginUrl: (integrationData: IntegrationData, state?: string) => {
				const stateQueryString = state ? `&state=${encodeURIComponent(state)}` : ""
				const encodedRedirectUri = encodeURIComponent(`${window.location.origin}/oauthcallback`)
				const redirectQueryString = `&redirect_uri=${encodedRedirectUri}`
				const scope = "offline_access read_candidate write_candidate read_user partner_ui_action"
				const scopeQueryString = `&scope=${encodeURIComponent(scope)}`
				return `https://id.jobadder.com/connect/authorize?client_id=${jobAdderClientId}&response_type=code${scopeQueryString}${redirectQueryString}${stateQueryString}`
			},
		},
	},
	initialData: {},
	useFrontendFileUpload: false,
}

export default jobAdderIntegrationData
